



















































































import { Vue, Prop, Component } from 'vue-property-decorator';
import {
    ProductDetailsViewModel,
    ImageViewObject,
    VideoViewObject,
    CategoryFallbackViewObject
} from '@/types/apiServerContract';
import breakpointsState from '@/core/responsive/breakpoints/breakpointsState.observable';
import ProductDetailsImageVideo from './ProductDetailsImageVideo.vue';
import { PageName } from '@/router/routes/constants';
import { MAX_SM } from '@/core/config/breakpointConfig';
import productDetailsMediaOverlayService from '@/project/modals/overlays/ProductDetailsMediaOverlay.service';
import botService from '@/project/site/bot.service';

const THUMBNAIL_SLIDES_PER_VIEW = 3;
const isBot: boolean = !!((botService.uaIsPrerender() || botService.uaIsBot()));

@Component({
    components: { ProductDetailsImageVideo },
    $reactiveRefs: ['slider', 'thumbs']
})
export default class ProductDetailsSlider extends Vue {
    @Prop({ required: true }) details!: ProductDetailsViewModel;

    /* Can take a single aspectratio number, or an object with a number pr. breakpoint. If a 'default' is provided is will be used when no breakpoint is active:
        {
             xs: 16/9,
             min-sm,max-md: 4/3
             default: 1/2
        }
     */
    @Prop({ type: [Number, Object], default: 16 / 9 })
    aspectRatio!: number | { [breakpoint: string]: number};

    // Slider
    renderMainSlider: boolean = false;
    mainSliderActive: boolean = false;
    overlayActive: boolean = false;

    get style(): object {
        const padding = `${(100 / this.activeAspectRatio).toFixed(2)}%`;
        return { paddingTop: padding };
    }

    get productName() {
        return this.details.displayName + ' - ' + this.details.wid;
    }

    get assetCount(): number {
        return (this.productImages.length + this.productVideos.length);
    }

    get activeAspectRatio(): number {
        if (typeof this.aspectRatio === 'number') {
            return this.aspectRatio;
        }

        // Else - object with aspect-ratio pr. breakpoint-def.

        // Find first active breakpoint-def
        const activeBpDef = Object.keys(this.aspectRatio).find(bpDef => breakpointsState.isBreakpointActive(bpDef));
        // Use active or default
        const result = activeBpDef ? this.aspectRatio[activeBpDef] : this.aspectRatio.default;
        if (!result) {
            throw new Error('Provide a breakpoint-definition for all cases or a default');
        }
        return result;
    }

    get prevButtonSelector() {
        return 'custom-swiper-button-prev-' + this.details.wid;
    }

    get nextButtonSelector() {
        return 'custom-swiper-button-next-' + this.details.wid;
    }

    get isMobile() {
        return breakpointsState.isBreakpointActive(MAX_SM);
    }

    get showThumbNails() {
        return !this.isMobile && this.assetCount > 1;
    }

    get sliderOptions() {
        if (this.showThumbNails && !this.$refs.thumbs) {
            return null;
        }

        return ({
            speed: 1,
            initialSlide: 0,
            slidesPerView: 1,
            slidesPerGroup: 1,
            loop: false,
            loopedSlides: 1,
            loopFillGroupWithBlank: false,
            watchSlidesVisibility: true,
            watchOverflow: true,
            setWrapperSize: true,
            pagination: {
                el: '.swiper-pagination',
                clickable: true
            },
            navigation: {
                nextEl: '.' + this.nextButtonSelector,
                prevEl: '.' + this.prevButtonSelector
            },
            grabCursor: true,
            allowTouchMove: true,
            breakpoints: {
                720: {
                    grabCursor: false,
                    allowTouchMove: false,
                    simulateTouch: false
                }
            },
            thumbs: {
                swiper: this.showThumbNails && this.$refs.thumbs && this.$refs.thumbs.$el.swiper
            }
        });
    };

    get sliderThumnailOptions() {
        return {
            initialSlide: 0,
            slidesPerView: THUMBNAIL_SLIDES_PER_VIEW,
            slidesPerGroup: 1,
            spaceBetween: 10,
            grabCursor: true,
            loop: false,
            loopFillGroupWithBlank: false,
            watchSlidesVisibility: true,
            watchOverflow: true,
            setWrapperSize: true,
            navigation: {
                nextEl: '.' + this.nextButtonSelector,
                prevEl: '.' + this.prevButtonSelector
            },
            height: '140px',
            keyboard: {
                enabled: true,
                onlyInViewPort: true
            }
        };
    }

    $refs!: Vue['$refs'] & {
        slider: any, // todo: typesafety
        thumbs: any
    };

    showOverlay() {
        if (this.isMobile) return;

        const index = this.$refs.slider.$swiper.activeIndex;

        productDetailsMediaOverlayService.show(
            this.productImages,
            this.productVideos,
            this.details.brandLogos,
            this.details.brand,
            16 / 9,
            this.productName,
            this.$refs.slider,
            index);
    }

    get productImages(): ImageViewObject[] {
        return this.details?.images ?? [];
    }

    get productVideos(): VideoViewObject[] {
        return this.details?.videoes ?? [];
    }

    get brandPage() {
        return PageName.BRAND;
    }

    created() {
        if (this.details.brandLogos) {
            // Unfortunately we don't receive image dimensions on the brandLogo object
            // Therefore we load a new image based on the uri to get the dimensions when the image has loaded
            this.imageDimensions(this.details.brandLogos.brandLogoUrl125px);
        }

        // Always render it if thumbnails isnt needed
        this.renderMainSlider = !this.showThumbNails;
    }

    imageMode: 'wide' | 'tall' | 'square' = 'wide';
    imageLoaded: boolean = isBot; // if bot, set imageLoaded = true, else false and let it trigger when images are loaded

    imageDimensions(url) {
        const img = new Image();

        img.src = url;

        img.onload = () => {
            if (img.width === img.height) {
                this.imageMode = 'square';
            } else if (img.width > img.height) {
                this.imageMode = 'wide';
            } else {
                this.imageMode = 'tall';
            }

            this.imageLoaded = true;
        };
    }

    get fallbackImage(): CategoryFallbackViewObject | ImageViewObject | null {
        let categoryFallbackImage: CategoryFallbackViewObject | null = null;
        if (this.details.categoryFallbackImageMap) {
            Object.keys(this.details.categoryFallbackImageMap).forEach((index, key) => {
                const item = this.details.categoryFallbackImageMap[index];
                if (!categoryFallbackImage && item.isDefaultFallback) {
                    categoryFallbackImage = item;
                }
            });
        }
        // Return categoryFallbackImageMap or old categoryFallbackImage (for legacy only) or null
        return categoryFallbackImage || this.details?.categoryFallbackImage || null;
    }
}
